import { useState } from "react";
import { toast } from "sonner";
import { MailCheck, Smartphone, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AccountVerification() {
  const { user, refreshUser } = useAuth();
  const [emailStep, setEmailStep] = useState("idle"); // idle | sent
  const [phoneStep, setPhoneStep] = useState("idle"); // idle | sent
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState("");

  const run = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const inputCls = "h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600";
  const btnCls = "h-10 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200";
  const ghostCls = "h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 disabled:opacity-50 transition-colors duration-200";

  return (
    <div className="glass rounded-lg p-6 sm:p-8" data-testid="account-verification">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4 w-4 text-blue-600" />
        <h2 className="font-display text-lg font-bold text-zinc-950">Account security</h2>
      </div>
      <p className="text-sm text-zinc-500 mt-1 mb-5">
        Verifying your email and mobile helps keep every account genuine. It's quick, and you only do it once.
      </p>
      <div className="grid sm:grid-cols-2 gap-6 items-start">
        <div data-testid="verify-email-block">
          <div className="flex items-center gap-2">
            <MailCheck className="h-4 w-4 text-zinc-500" />
            <p className="text-sm font-semibold text-zinc-900">Email address</p>
            {user?.email_verified ? (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700 px-2 py-0.5 rounded" data-testid="email-verified-badge">Verified</span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded" data-testid="email-unverified-badge">Not verified</span>
            )}
          </div>
          {!user?.email_verified && (
            emailStep === "sent" ? (
              <div className="flex flex-wrap gap-2 mt-3">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
                  data-testid="email-code-input" maxLength={6} inputMode="numeric" className={`${inputCls} w-36`} />
                <button disabled={busy === "email-confirm" || code.trim().length !== 6}
                  onClick={() => run("email-confirm", async () => {
                    await api.post("/auth/verify/email/confirm", { code: code.trim() });
                    await refreshUser();
                  }, "Email verified. Thank you.")}
                  data-testid="email-confirm-btn" className={btnCls}>
                  {busy === "email-confirm" ? "Checking…" : "Confirm"}
                </button>
                <button disabled={busy === "email-send"}
                  onClick={() => run("email-send", () => api.post("/auth/verify/email/send"), "Fresh code sent.")}
                  data-testid="email-resend-btn" className={ghostCls}>
                  Resend
                </button>
              </div>
            ) : (
              <button disabled={busy === "email-send"}
                onClick={() => run("email-send", async () => { await api.post("/auth/verify/email/send"); setEmailStep("sent"); }, "Code sent, check your inbox.")}
                data-testid="email-verify-btn" className={`${btnCls} mt-3`}>
                Email me a code
              </button>
            )
          )}
          {!user?.email_verified && <p className="text-xs text-zinc-400 mt-2">Codes expire after 15 minutes.</p>}
        </div>

        <div data-testid="verify-phone-block">
          <div className="flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-zinc-500" />
            <p className="text-sm font-semibold text-zinc-900">Mobile number</p>
            {user?.phone_verified ? (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700 px-2 py-0.5 rounded" data-testid="phone-verified-badge">Verified</span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded" data-testid="phone-unverified-badge">Optional</span>
            )}
          </div>
          {user?.phone_verified ? (
            <p className="text-xs text-zinc-500 mt-2" data-testid="phone-masked">{user.phone_masked}</p>
          ) : !user?.sms_available ? (
            <p className="text-xs text-zinc-400 mt-2" data-testid="sms-unavailable">Text-message verification is being set up and will be available shortly.</p>
          ) : phoneStep === "sent" ? (
            <div className="flex flex-wrap gap-2 mt-3">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
                data-testid="phone-code-input" maxLength={6} inputMode="numeric" className={`${inputCls} w-36`} />
              <button disabled={busy === "phone-confirm" || code.trim().length !== 6}
                onClick={() => run("phone-confirm", async () => {
                  await api.post("/auth/verify/phone/confirm", { code: code.trim() });
                  await refreshUser();
                }, "Mobile verified. Thank you.")}
                data-testid="phone-confirm-btn" className={btnCls}>
                {busy === "phone-confirm" ? "Checking…" : "Confirm"}
              </button>
              <button onClick={() => { setPhoneStep("idle"); setCode(""); }} data-testid="phone-change-btn" className={ghostCls}>
                Change number
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-3">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+447700900123"
                data-testid="phone-input" type="tel" className={`${inputCls} flex-1 min-w-[10rem]`} />
              <button disabled={busy === "phone-start" || !phone.trim()}
                onClick={() => run("phone-start", async () => { await api.post("/auth/verify/phone/start", { phone: phone.trim() }); setPhoneStep("sent"); setCode(""); }, "Code sent by text message.")}
                data-testid="phone-send-btn" className={btnCls}>
                Text me a code
              </button>
            </div>
          )}
          {!user?.phone_verified && user?.sms_available && (
            <p className="text-xs text-zinc-400 mt-2">We only ever show your number masked, like +••• ••• ••23.</p>
          )}
        </div>
      </div>
    </div>
  );
}
